import {use, useMemo} from 'react'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {CurrentUrlContext} from '../../contexts/CurrentUrlContext'
import {matchesCurrentPage} from '../utils/current-page'

export interface PageMatcher {
  /** Returns true when `href` resolves to the page currently being viewed. */
  isCurrentPage: (href: string) => boolean
}

/**
 * Provides an SSR-safe way to test whether a link points at the current page,
 * so components can set `aria-current="page"` consistently during both server
 * and client rendering.
 *
 * The current URL comes from `ssrSafeLocation.href` (the real `location` on the
 * client, the request URL from AsyncLocalStorage during Alloy SSR). Tests and
 * stories may override it via {@link CurrentUrlContext}.
 */
export function usePageMatcher(): PageMatcher {
  const overrideUrl = use(CurrentUrlContext)
  const currentUrl = overrideUrl ?? ssrSafeLocation.href

  return useMemo(
    () => ({
      isCurrentPage: (href: string) => matchesCurrentPage(href, currentUrl),
    }),
    [currentUrl],
  )
}
